import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Button, IconButton, Menu, MenuItem, ListItemIcon, CircularProgress, Alert, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar,
} from '@mui/material';
import {
  ArrowBackRounded, MoreVertRounded, EditRounded, ContentCopyRounded, PauseCircleRounded,
  QrCode2Rounded, InsightsRounded, ScheduleRounded,
  PlayArrowRounded, StopRounded, PlayCircleRounded,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { colors, brand } from '../../theme/theme';
import { useQueues } from '../../hooks/useQueues';
import { useHolidays } from '../../hooks/useHolidays';
import { Queue, BookingWindow, DayOfWeek } from '../../api/queueTypes';
import { Holiday } from '../../api/holidayTypes';
import { canManageQueueSettings } from '../../auth/session';
import { QueueStatusChip, ModeBadge } from './queueParts';
import TokensSection from './TokensSection';
import QueueDateStrip from './QueueDateStrip';
import PauseQueueDialog from './PauseQueueDialog';

const card = { bgcolor: colors.backgroundPaper, border: `1px solid ${colors.border}`, borderRadius: '14px' };

const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
const WEEKDAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const QueueDetailsPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getQueue, duplicateQueue, getBookingWindow, openBookingWindow, closeBookingWindow } = useQueues();
  const { listHolidays, deleteHoliday } = useHolidays();

  const [queue, setQueue] = useState<Queue | null>(null);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(startOfToday);
  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupName, setDupName] = useState('');
  const [qrOpen, setQrOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [window_, setWindow] = useState<BookingWindow | null>(null);
  const [windowBusy, setWindowBusy] = useState(false);
  const [dayBooked, setDayBooked] = useState(0); // non-cancelled tokens on the selected date
  const [pauseOpen, setPauseOpen] = useState(false);

  // Edit / Duplicate / Pause — allowed for Administrator, Admin and Provider
  // (everyone except Supervisor), matching the mobile app.
  const canManage = canManageQueueSettings();
  const dateStr = ymd(selectedDate);
  const todayStr = ymd(startOfToday());
  const isToday = dateStr === todayStr;
  const isPast = dateStr < todayStr;

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true); setError(null);
    try {
      setQueue(await getQueue(id));
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load queue.');
    } finally { setLoading(false); }
  }, [id, getQueue]);

  useEffect(() => { load(); }, [load]);

  // Holidays for this queue (to flag closed/holiday/paused dates) — mirrors mobile,
  // where "pause" == a holiday on the queue. Reloadable so pause/resume refresh it.
  const loadHolidays = useCallback(() => {
    if (!id) return;
    listHolidays(id).then(setHolidays).catch(() => {});
  }, [id, listHolidays]);
  useEffect(() => { loadHolidays(); }, [loadHolidays]);

  // Date-based availability (mirrors mobile: schedule day + holiday range + capacity).
  const DAY_NAMES: DayOfWeek[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const daySchedule = useMemo(() => {
    if (!queue) return null;
    const weekday = DAY_NAMES[new Date(dateStr + 'T00:00:00').getDay()];
    return queue.schedule.find((s) => s.day.toLowerCase() === weekday) || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue, dateStr]);
  const isClosedDay = !daySchedule || !daySchedule.is_enabled || !daySchedule.start_time;
  const maxTokensForDay = daySchedule?.max_tokens ?? null;
  // A holiday covering the selected date == the queue is paused for that date (mobile model).
  const currentHoliday = useMemo(
    () => holidays.find((h) => dateStr >= h.start_date && dateStr <= h.end_date) || null,
    [holidays, dateStr]
  );
  const isHoliday = !!currentHoliday;
  // Weekday indices (0=Sun) the queue is NOT scheduled → greyed out in the strip.
  const disabledWeekdays = useMemo(() => {
    if (!queue) return [];
    return WEEKDAY_NAMES.map((name, idx) => {
      const s = queue.schedule.find((x) => x.day.toLowerCase() === name);
      return !s || !s.is_enabled || !s.start_time ? idx : -1;
    }).filter((i) => i >= 0);
  }, [queue]);

  // Booking window status for controlled_public queues on the selected date
  useEffect(() => {
    if (!id || queue?.booking_mode !== 'controlled_public') { setWindow(null); return; }
    let active = true;
    (async () => {
      try {
        const list = await getBookingWindow(id, dateStr, dateStr);
        if (active) setWindow(list.find((w) => w.window_date === dateStr) || list[0] || null);
      } catch { if (active) setWindow(null); }
    })();
    return () => { active = false; };
  }, [id, queue?.booking_mode, dateStr, getBookingWindow]);

  const toggleWindow = async () => {
    if (!id) return;
    setWindowBusy(true);
    try {
      if (window_?.is_open) { await closeBookingWindow(id, dateStr); setToast('Bookings stopped'); }
      else { await openBookingWindow(id, dateStr); setToast('Bookings opened'); }
      const list = await getBookingWindow(id, dateStr, dateStr);
      setWindow(list.find((w) => w.window_date === dateStr) || list[0] || null);
    } catch (e: any) {
      setToast(e?.response?.data?.message || 'Failed to update booking window');
    } finally { setWindowBusy(false); }
  };

  const doDuplicate = async () => {
    if (!id) return;
    try {
      await duplicateQueue(id, dupName.trim() || `${queue?.name} (Copy)`);
      setDupOpen(false); setToast('Queue duplicated');
      setTimeout(() => navigate('/queues'), 600);
    } catch (e: any) { setToast(e?.response?.data?.message || 'Failed to duplicate'); }
  };

  // Resume = delete the holiday covering the selected date (mobile model).
  const handleResume = async () => {
    if (!currentHoliday) return;
    try { await deleteHoliday(currentHoliday.id); setToast('Queue resumed'); loadHolidays(); }
    catch (e: any) { setToast(e?.response?.data?.message || 'Failed to resume'); }
  };

  if (loading) return <Box sx={{ display: 'grid', placeItems: 'center', py: 10 }}><CircularProgress /></Box>;
  if (error || !queue) return <Alert severity="error">{error || 'Queue not found.'}</Alert>;

  return (
    <Box>
      {/* Top bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <Button startIcon={<ArrowBackRounded />} variant="text" onClick={() => navigate('/queues')}>Queues</Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {queue.booking_mode === 'controlled_public' && (
            <Button
              variant="contained" disableElevation disabled={windowBusy}
              color={window_?.is_open ? 'error' : 'success'}
              startIcon={window_?.is_open ? <StopRounded /> : <PlayArrowRounded />}
              onClick={toggleWindow}
              sx={{ height: 40, whiteSpace: 'nowrap' }}
            >
              {window_?.is_open ? 'Stop bookings' : 'Start bookings'}
            </Button>
          )}
          {canManage && (
            <>
              <IconButton onClick={(e) => setMenuEl(e.currentTarget)} sx={{ width: 40, height: 40, border: `1px solid ${colors.border}`, borderRadius: '10px' }}>
                <MoreVertRounded />
              </IconButton>
              <Menu anchorEl={menuEl} open={!!menuEl} onClose={() => setMenuEl(null)} transformOrigin={{ horizontal: 'right', vertical: 'top' }} anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
                <MenuItem onClick={() => { setMenuEl(null); navigate(`/queues/${id}/edit`); }}><ListItemIcon><EditRounded fontSize="small" /></ListItemIcon>Edit queue</MenuItem>
                <MenuItem onClick={() => { setMenuEl(null); setDupName(`${queue.name} (Copy)`); setDupOpen(true); }}><ListItemIcon><ContentCopyRounded fontSize="small" /></ListItemIcon>Duplicate</MenuItem>
                <MenuItem onClick={() => { setMenuEl(null); setQrOpen(true); }}><ListItemIcon><QrCode2Rounded fontSize="small" /></ListItemIcon>Share QR</MenuItem>
                <MenuItem onClick={() => { setMenuEl(null); navigate('/revenue'); }}><ListItemIcon><InsightsRounded fontSize="small" /></ListItemIcon>View stats</MenuItem>
                {isHoliday ? (
                  <MenuItem onClick={() => { setMenuEl(null); handleResume(); }}><ListItemIcon><PlayCircleRounded fontSize="small" /></ListItemIcon>Resume queue</MenuItem>
                ) : (
                  <MenuItem onClick={() => { setMenuEl(null); setPauseOpen(true); }}><ListItemIcon><PauseCircleRounded fontSize="small" /></ListItemIcon>Pause queue</MenuItem>
                )}
              </Menu>
            </>
          )}
        </Box>
      </Box>

      {/* Header card */}
      <Box sx={{ ...card, p: 2.5, mb: 2.25 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h2" sx={{ fontSize: 22 }}>{queue.name}</Typography>
              <QueueStatusChip status={queue.status} />
              <ModeBadge mode={queue.booking_mode} withLabel />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: colors.textSecondary, mt: 0.5 }}>
              <ScheduleRounded sx={{ fontSize: 15 }} />
              <Typography sx={{ fontSize: 13 }}>
                {queue.schedule_summary || '—'} • {queue.providers.map((p) => p.display_name).filter(Boolean).join(', ') || 'No providers'}
              </Typography>
            </Box>
          </Box>
        </Box>
        {/* Date strip — calendar jump + scrollable day chips (mirrors mobile) */}
        <Box sx={{ mt: 2 }}>
          <QueueDateStrip selectedDate={selectedDate} onSelect={setSelectedDate} disabledWeekdays={disabledWeekdays} holidays={holidays} />
        </Box>
        {/* Selected-date status: Holiday takes precedence, then Closed */}
        {(isHoliday || isClosedDay) && (
          <Box sx={{ mt: 1.5 }}>
            <Chip
              size="small"
              label={isHoliday ? 'Holiday — queue closed' : 'Closed on this day'}
              sx={{ bgcolor: isHoliday ? '#FEF2F2' : colors.gray100, color: isHoliday ? colors.error : colors.gray600, fontWeight: 600 }}
            />
          </Box>
        )}
        {/* Capacity for the SELECTED date (booked on that day / that day's max) */}
        {!isHoliday && !isClosedDay && (() => {
          const max = maxTokensForDay ?? 0;
          const pct = max ? Math.min(100, Math.round((dayBooked / max) * 100)) : 0;
          const color = pct > 80 ? brand.capacity.high : pct >= 50 ? brand.capacity.medium : brand.capacity.low;
          const label = pct > 80 ? 'High demand' : pct >= 50 ? 'Medium demand' : 'Low demand';
          return (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ height: 8, borderRadius: 99, bgcolor: colors.gray100, overflow: 'hidden' }}>
                <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: color, borderRadius: 99, transition: 'width .4s' }} />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
                <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>{label}</Typography>
                <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>{dayBooked}/{max} • {pct}%</Typography>
              </Box>
            </Box>
          );
        })()}
      </Box>

      {/* Tokens */}
      <TokensSection
        queueId={queue.id}
        date={dateStr}
        providers={queue.providers}
        consultationEnabled={queue.consultation_enabled}
        onStats={(s) => setDayBooked(s.booked)}
        /* Actions (serve/skip/cancel/complete): today + active only, and never on a holiday. */
        readOnly={!isToday || queue.status !== 'active' || isHoliday}
        /* Walk-ins: today + future (not past) on an active queue; disabled (not hidden) on closed/holiday/full. */
        canAddWalkIn={!isPast && queue.status === 'active'}
        maxTokensForDay={maxTokensForDay}
        walkInDisabledReason={
          isHoliday ? 'This date is a holiday — the queue is closed.'
            : isClosedDay ? 'The queue is closed on this day.'
            : undefined
        }
        readOnlyReason={
          queue.status !== 'active'
            ? 'This queue is not active — activate it to manage tokens.'
            : isHoliday
            ? 'This date is a holiday — the queue is closed.'
            : !isToday && !isPast
            ? 'Future date — you can pre-add walk-in tokens; serve/skip actions are available only on the day.'
            : isPast
            ? 'Past date — read-only.'
            : undefined
        }
      />

      {/* Duplicate dialog */}
      <Dialog open={dupOpen} onClose={() => setDupOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Duplicate queue</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label="New queue name" value={dupName} onChange={(e) => setDupName(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDupOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={doDuplicate}>Duplicate</Button>
        </DialogActions>
      </Dialog>

      {/* QR dialog */}
      <Dialog open={qrOpen} onClose={() => setQrOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Share queue QR</DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          {/* Encodes the same payload as the mobile app: {"queue_id":"..."} */}
          <Box sx={{ display: 'inline-flex', p: 2, bgcolor: '#fff', borderRadius: '12px', border: `1px solid ${colors.border}`, boxShadow: '0 6px 18px rgba(15,23,42,.12)' }}>
            <QRCodeSVG value={JSON.stringify({ queue_id: queue.id })} size={200} level="M" />
          </Box>
          <Typography sx={{ mt: 2, fontWeight: 700 }}>{queue.name}</Typography>
          <Typography variant="caption" color="text.secondary">Customers scan this to join the queue.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrOpen(false)}>Close</Button>
          <Button variant="contained" onClick={() => { navigator.clipboard?.writeText(queue.id); setToast('Queue ID copied'); }}>Copy ID</Button>
        </DialogActions>
      </Dialog>

      {pauseOpen && (
        <PauseQueueDialog
          queueId={queue.id}
          queueName={queue.name}
          defaultDate={selectedDate}
          onClose={() => setPauseOpen(false)}
          onPaused={(msg) => { setPauseOpen(false); setToast(msg); loadHolidays(); }}
        />
      )}

      <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default QueueDetailsPage;
